import { SitecoreDictionaryItem, SitecoreDictionaryPhrase } from '../models';

/**
 * Retrieves a shared dictionary field value from the given fields object.
 * Resolves localized, shared, and dictionary variants in priority order.
 *
 * @param fields - The fields object containing the dictionary fields.
 * @param fieldName - The name of the field to retrieve.
 * @returns The resolved field, or undefined if not found/filled.
 */
export function getSharedDictionary<
  T extends Record<
    K,
    { value?: unknown } | { jsonValue: SitecoreDictionaryItem; fields: SitecoreDictionaryPhrase }
  >,
  K extends keyof T,
>(fields: T, fieldName: K): T[K] | undefined {
  const field = fields?.[fieldName];

  if (field && 'value' in field && field.value) {
    return field;
  }

  const sharedString = `${fieldName as string}Shared` as keyof T;
  const dictionaryString = `${fieldName as string}Dictionary` as keyof T;
  const sharedField = fields?.[sharedString] ?? fields?.[dictionaryString] ?? fields?.[fieldName];

  if (typeof sharedField === 'string') {
    console.error(
      `ERROR reading shared field ${sharedString.toString()}. Got only ID instead of value. Probably orphaned reference or indexing issue`
    );
    return undefined;
  }

  if (sharedField && 'fields' in sharedField) {
    const phrase = sharedField.fields?.Phrase;
    const phraseShared = sharedField.fields?.PhraseShared;

    if (phrase?.value || phraseShared?.value) {
      return {
        ...sharedField,
        value: phrase?.value || phraseShared?.value || '',
      } as unknown as T[K];
    }
  }

  if (sharedField && 'jsonValue' in sharedField) {
    const jsonValueField = sharedField.jsonValue;

    const phrase = jsonValueField?.fields?.Phrase;
    const phraseShared = jsonValueField?.fields?.PhraseShared;

    if (phrase?.value || phraseShared?.value) {
      return {
        ...sharedField,
        value: phrase?.value || phraseShared?.value || '',
      } as unknown as T[K];
    }
  }

  return undefined;
}
